import { useLocalStorage } from "@elit-hair/elit-tools";
import { DateTime } from "luxon";
import { SetStateAction, useCallback, useMemo } from "react";

import { showNotificationToast } from "@/components/notifications/notification-toast";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { IncidentReviewingNotification, IncidentReviewingNotificationPayload } from "@/types/incident.types";

import {
    StoredIncidentNotification,
    deserializeIncidents,
    serializeIncidents,
    toStoredIncident,
} from "./persisted-notifications";

export const useIncidentNotification = () => {
    const [storedNotifications, setStoredNotifications] = useLocalStorage<StoredIncidentNotification[]>(
        LOCAL_STORAGE_KEYS.PENDING_INCIDENT_NOTIFICATIONS.key,
        LOCAL_STORAGE_KEYS.PENDING_INCIDENT_NOTIFICATIONS.default
    );

    const pendingIncidentNotifications = useMemo(
        () => deserializeIncidents(storedNotifications),
        [storedNotifications]
    );

    const setPendingIncidentNotifications = useCallback(
        (value: SetStateAction<IncidentReviewingNotification[]>) => {
            setStoredNotifications((prev) => {
                const current = deserializeIncidents(prev);
                const next = typeof value === "function" ? value(current) : value;
                return serializeIncidents(next);
            });
        },
        [setStoredNotifications]
    );

    const dismissIncidentNotification = useCallback(
        (incidentId: string): void => {
            setPendingIncidentNotifications((prev) => prev.filter((n) => n.incidentId !== incidentId));
        },
        [setPendingIncidentNotifications]
    );

    const handleIncidentReviewingNotification = (data: IncidentReviewingNotificationPayload): void => {
        const notification: IncidentReviewingNotification = {
            id: data.incidentId,
            incidentId: data.incidentId,
            title: data.title,
            body: data.body,
            type: data.type,
            receivedAt: DateTime.now(),
        };

        setStoredNotifications((prev) => {
            const withoutDuplicate = prev.filter((n) => n.incidentId !== notification.incidentId);
            return [toStoredIncident(notification), ...withoutDuplicate];
        });

        showNotificationToast({
            variant: "incident",
            title: notification.title,
            description: notification.body,
            duration: 8000,
        });
    };

    return {
        pendingIncidentNotifications,
        setPendingIncidentNotifications,
        handleIncidentReviewingNotification,
        dismissIncidentNotification,
    };
};
